import {
  formatChartRowLabel,
  formatChartSeriesLabel,
  formatChartTickLabel,
  getChartValueFormatter,
} from './chartFormatters'
import { getGroupbyFieldLabel } from './collectionGroupby'
import { getMetricDefinition } from './collectionMetrics'
import { MONEY_KEYS } from './metricColumns'

const TIME_FIELD_BASES = new Set(['hour', 'day', 'weekday', 'week', 'month', 'year'])

export const DIMENSION_SEPARATOR = ' · '

export function nextChartId(charts = []) {
  const highest = charts.reduce((acc, chart) => {
    const id = Number(chart?.id)
    return Number.isFinite(id) && id > acc ? id : acc
  }, 0)

  return highest + 1
}

const INDEX_COLLAPSING_TYPES = new Set(['pie-chart', 'funnel-chart'])

export function applyChartFilter(result, filter) {
  const hiddenKeys = new Set(filter?.keys || [])
  const hiddenIndexes = new Set(filter?.indexes || [])
  if (!hiddenKeys.size && !hiddenIndexes.size) return result

  const keys = (result.keys || []).filter(key => !hiddenKeys.has(key))

  const data = (result.data || [])
    .filter(row => !hiddenIndexes.has(row.date))
    .map(row => keys.reduce((next, key) => ({ ...next, [key]: row[key] }), { date: row.date }))

  const slices = keys.map(key => ({
    id: key,
    value: data.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
  }))

  const filtered = {
    ...result,
    keys,
    data,
    slices,
    unsupportedKeys: (result.unsupportedKeys || []).filter(key => !hiddenKeys.has(key)),
  }

  if (result.keyType !== 'metric') return filtered

  const type = metricType(keys[0])

  return { ...filtered, type, format: getChartValueFormatter(type) }
}

export function collapsesChartIndex(dataType) {
  return INDEX_COLLAPSING_TYPES.has(dataType)
}

const NON_ADDITIVE_TYPES = new Set([
  'cardinality',
  'avg',
  'min',
  'max',
  'stats',
  'extended_stats',
  'percentiles',
])

export function getGroupbyBaseField(field) {
  return String(field ?? '').split(':')[0].trim()
}

export function isTimeGroupbyField(field) {
  if (!field) return false

  return TIME_FIELD_BASES.has(getGroupbyBaseField(field).replace(/_(session|unittime)$/, ''))
}

export function getTimeGroupbyField(groupby = []) {
  return groupby.find(isTimeGroupbyField)
}

export function getXAxisFields(groupby = []) {
  return groupby.filter(isTimeGroupbyField)
}

export function axisAcceptsAnyGroupby(dataType) {
  return dataType === 'bar-chart'
}

export function isAxisFieldAllowed(field, dataType) {
  if (!field) return false
  if (axisAcceptsAnyGroupby(dataType)) return true

  return isTimeGroupbyField(field)
}

export function getGroupbyLabel(collection, field) {
  return getGroupbyFieldLabel(collection, field)
}

export function isAdditiveMetric(collection, key) {
  const definition = getMetricDefinition(collection, key)
  if (!definition) return false
  if (definition.derived) return false
  return !NON_ADDITIVE_TYPES.has(definition.type)
}

function legacyDimensions(reportGroupby) {
  const timeField = getTimeGroupbyField(reportGroupby)
  return timeField ? [timeField] : reportGroupby.slice(0, 1)
}

export function resolveChartDimensions(chart, reportGroupby = []) {
  const selected = chart?.dimensions?.length ? chart.dimensions : legacyDimensions(reportGroupby)

  const dimensions = selected
    .filter(field => reportGroupby.includes(field))
    .sort((a, b) => reportGroupby.indexOf(a) - reportGroupby.indexOf(b))

  if (dimensions.length) return dimensions.slice(0, 1)

  return reportGroupby.length ? [reportGroupby[0]] : []
}

export function resolveChartSeriesBy(chart, reportGroupby = [], dimensions = []) {
  const seriesBy = chart?.seriesBy
  if (!seriesBy || !reportGroupby.includes(seriesBy)) return null
  if (dimensions.includes(seriesBy)) return null
  return seriesBy
}

function metricValue(row, key) {
  return Number(row?.[key]) || 0
}

function metricType(key) {
  if (!key) return 'count'
  if (key.endsWith('PC')) return 'percentage'
  if (MONEY_KEYS.has(key)) return 'money'
  return 'count'
}

function rowsAtDepth(nodes, targetDepth, currentDepth = 0) {
  if (!nodes?.length) return []
  if (currentDepth === targetDepth) return nodes
  return nodes.flatMap(node => rowsAtDepth(node.children || [], targetDepth, currentDepth + 1))
}

function collectPathNodes(node) {
  const nodes = {}
  let current = node

  while (current) {
    if (current.key && nodes[current.key] === undefined) {
      nodes[current.key] = current
    }
    current = current.parent
  }

  return nodes
}

function accumulate(values, key, value) {
  const entry = values.get(key)
  if (entry) {
    entry.total += value
    entry.sources += 1
    return
  }
  values.set(key, { total: value, sources: 1 })
}

function emptyResult() {
  return {
    data: [],
    slices: [],
    keys: [],
    type: 'count',
    format: getChartValueFormatter('count'),
    keyType: 'metric',
    indexLabel: '',
    indexTicks: {},
    unsupportedKeys: [],
  }
}

export function buildChartFromTable(tableData, chart, reportGroupby = [], collection) {
  if (!tableData?.children?.length) return emptyResult()

  const collapsesIndex = collapsesChartIndex(chart?.dataType)
  const dimensions = collapsesIndex ? [] : resolveChartDimensions(chart, reportGroupby)
  if (!collapsesIndex && !dimensions.length) return emptyResult()

  const seriesBy = resolveChartSeriesBy(chart, reportGroupby, dimensions)
  const metricKeys = (chart?.metricKeys || []).filter(Boolean)
  const primaryMetric = chart?.breakdown || metricKeys[0]

  if (!metricKeys.length) return emptyResult()

  const usedFields = seriesBy ? [...dimensions, seriesBy] : dimensions
  const deepestIndex = usedFields.length ? Math.max(...usedFields.map(field => reportGroupby.indexOf(field))) : 0
  const nodes = rowsAtDepth(tableData.children, deepestIndex)

  const needsAggregation = collapsesIndex ? nodes.length > 1 : usedFields.length < deepestIndex + 1

  const buckets = new Map()
  const seriesKeys = new Set()
  const bucketSortKeys = new Map()
  const bucketTicks = new Map()
  const timeDimension = dimensions.length === 1 && isTimeGroupbyField(dimensions[0]) ? dimensions[0] : null

  nodes.forEach(node => {
    const pathNodes = collectPathNodes(node)
    const bucketLabel = dimensions
      .map(field => pathNodes[field] ? formatChartRowLabel(pathNodes[field]) : '--')
      .join(DIMENSION_SEPARATOR)

    if (!buckets.has(bucketLabel)) {
      buckets.set(bucketLabel, new Map())
    }
    if (timeDimension && !bucketSortKeys.has(bucketLabel)) {
      bucketSortKeys.set(bucketLabel, Number(pathNodes[timeDimension]?.value) || 0)
      bucketTicks.set(bucketLabel, formatChartTickLabel(pathNodes[timeDimension]))
    }
    const values = buckets.get(bucketLabel)

    if (seriesBy) {
      const seriesNode = pathNodes[seriesBy]
      const seriesKey = seriesNode ? formatChartSeriesLabel(seriesNode) : '--'
      seriesKeys.add(seriesKey)
      accumulate(values, seriesKey, metricValue(node, primaryMetric))
      return
    }

    metricKeys.forEach(key => accumulate(values, key, metricValue(node, key)))
  })

  const aggregatedMetrics = seriesBy ? [primaryMetric] : metricKeys
  const unsupportedKeys = needsAggregation ? aggregatedMetrics.filter(key => !isAdditiveMetric(collection, key)) : []

  const keys = seriesBy ? [...seriesKeys] : metricKeys
  const droppedBySeries = Boolean(unsupportedKeys.length && seriesBy)
  const activeKeys = droppedBySeries ? [] : keys.filter(key => !unsupportedKeys.includes(key))

  const entries = [...buckets.entries()]

  if (timeDimension) {
    entries.sort(([a], [b]) => bucketSortKeys.get(a) - bucketSortKeys.get(b))
  }

  const data = entries.map(([bucketLabel, values]) => {
    const point = { date: bucketLabel }
    activeKeys.forEach(key => {
      point[key] = values.get(key)?.total ?? 0
    })
    return point
  })

  const slices = activeKeys.map(key => ({
    id: key,
    value: data.reduce((sum, point) => sum + (Number(point[key]) || 0), 0),
  }))

  const type = metricType(seriesBy ? primaryMetric : activeKeys[0])

  return {
    data,
    slices,
    keys: activeKeys,
    type,
    format: getChartValueFormatter(type),
    keyType: seriesBy ? 'entity' : 'metric',
    indexLabel: dimensions.map(field => getGroupbyLabel(collection, field)).join(DIMENSION_SEPARATOR),
    indexTicks: Object.fromEntries(bucketTicks),
    unsupportedKeys,
  }
}
